import { ChangeDetectionStrategy, Component, computed, inject, Signal, signal, WritableSignal } from '@angular/core';
import { FormControl, FormGroup, ReactiveFormsModule, Validators } from '@angular/forms';
import { Router } from '@angular/router';
import { TranslateModule, TranslateService } from '@ngx-translate/core';
import { firstValueFrom } from 'rxjs';
import { AppRoutes, ProfileRoutes } from '@core';
import { SzBrandBlockComponent, SzIconComponent } from '@ui';
import { ContactHttpService } from './contact-http.service';

const MESSAGE_MIN_LENGTH: number = 1;
const MESSAGE_MAX_LENGTH: number = 1000;

interface ContactFormShape {
  message: FormControl<string>;
}

@Component({
  selector: 'app-contact-page',
  templateUrl: './contact.page.html',
  styleUrls: ['./contact.page.scss'],
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: true,
  imports: [TranslateModule, ReactiveFormsModule, SzBrandBlockComponent, SzIconComponent]
})
export class ContactPage {
  readonly maxLength: number = MESSAGE_MAX_LENGTH;
  readonly profilePath: string = `/${AppRoutes.PROFILE}`;

  readonly form: FormGroup<ContactFormShape> = new FormGroup<ContactFormShape>({
    message: new FormControl<string>('', {
      nonNullable: true,
      validators: [
        Validators.required,
        Validators.minLength(MESSAGE_MIN_LENGTH),
        Validators.maxLength(MESSAGE_MAX_LENGTH)
      ]
    })
  });

  readonly loading: WritableSignal<boolean> = signal<boolean>(false);
  readonly errorMessage: WritableSignal<string | null> = signal<string | null>(null);
  readonly success: WritableSignal<boolean> = signal<boolean>(false);
  readonly value: WritableSignal<string> = signal<string>('');

  readonly remainingChars: Signal<number> = computed((): number => MESSAGE_MAX_LENGTH - this.value().length);

  private readonly router: Router = inject(Router);
  private readonly translateService: TranslateService = inject(TranslateService);
  private readonly contactHttp: ContactHttpService = inject(ContactHttpService);

  constructor() {
    this.form.controls.message.valueChanges.subscribe((v: string): void => this.value.set(v ?? ''));
  }

  goBack(): void {
    void this.router.navigateByUrl(this.profilePath);
  }

  async onSubmit(): Promise<void> {
    const raw: string = this.form.controls.message.value.trim();
    if (raw.length === 0 || this.form.invalid) {
      this.form.controls.message.markAsTouched();
      return;
    }
    this.loading.set(true);
    this.errorMessage.set(null);
    try {
      await firstValueFrom(this.contactHttp.send({ message: raw }));
      this.success.set(true);
      this.form.reset({ message: '' });
      this.value.set('');
    } catch (err: unknown) {
      const status: number | null = this.extractStatus(err);
      const key: string = status === 409 ? 'contact.error.duplicate' : 'contact.error.generic';
      this.errorMessage.set(this.translateService.instant(key));
    } finally {
      this.loading.set(false);
    }
  }

  // expose for template
  protected readonly ProfileRoutes: typeof ProfileRoutes = ProfileRoutes;

  private extractStatus(err: unknown): number | null {
    if (err && typeof err === 'object' && 'status' in err && typeof (err as { status: unknown }).status === 'number') {
      return (err as { status: number }).status;
    }
    return null;
  }
}
